import React, { useMemo, useState } from 'react';
import { CheckIcon, SparklesIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type {
  WorkspaceVerifiedQuery,
  WorkspaceVerifiedQueryCreate,
  WorkspaceVerifiedQueryType,
  WorkspaceVerifiedQueryUpdate,
} from '../../hooks/useWorkspaceSemanticContext';
import { Button, Badge, Input, Textarea, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../ui';

interface WorkspaceVerifiedQueriesSectionProps {
  queries: WorkspaceVerifiedQuery[];
  isLoading?: boolean;
  isDrafting?: boolean;
  isSaving?: boolean;
  isReviewing?: boolean;
  isDeleting?: boolean;
  onCreate: (payload: WorkspaceVerifiedQueryCreate) => Promise<unknown>;
  onDraftWithAI: (payload: { query_type: WorkspaceVerifiedQueryType; business_question: string }) => Promise<unknown>;
  onUpdate: (queryId: number, payload: WorkspaceVerifiedQueryUpdate) => Promise<unknown>;
  onApprove: (queryId: number) => Promise<unknown>;
  onReject: (queryId: number) => Promise<unknown>;
  onDelete: (queryId: number) => Promise<unknown>;
}

type Tab = 'structured' | 'unstructured';

export function WorkspaceVerifiedQueriesSection({
  queries,
  isLoading = false,
  isDrafting = false,
  isSaving = false,
  isReviewing = false,
  isDeleting = false,
  onCreate,
  onDraftWithAI,
  onUpdate,
  onApprove,
  onReject,
  onDelete,
}: WorkspaceVerifiedQueriesSectionProps) {
  const [tab, setTab] = useState<Tab>('structured');
  const [businessQuestion, setBusinessQuestion] = useState('');
  const [expectedContract, setExpectedContract] = useState('');
  const [sqlOrPseudocode, setSqlOrPseudocode] = useState('');
  const [tableRefs, setTableRefs] = useState('');
  const [columnRefs, setColumnRefs] = useState('');
  const [sourceRefs, setSourceRefs] = useState('');
  const [citationRules, setCitationRules] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editContract, setEditContract] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WorkspaceVerifiedQuery | null>(null);

  const filteredQueries = useMemo(
    () => queries.filter((query) => query.query_type === tab || (query.query_type === 'blended' && tab === 'structured')),
    [queries, tab],
  );

  const resetForm = () => {
    setBusinessQuestion('');
    setExpectedContract('');
    setSqlOrPseudocode('');
    setTableRefs('');
    setColumnRefs('');
    setSourceRefs('');
    setCitationRules('');
  };

  const buildPayload = (): WorkspaceVerifiedQueryCreate => {
    const refs = {
      table_refs: splitList(tableRefs),
      column_refs: splitList(columnRefs),
      source_refs: splitList(sourceRefs),
    };
    if (tab === 'structured') {
      return {
        query_type: 'structured',
        business_question: businessQuestion,
        pseudocode: sqlOrPseudocode || null,
        expected_answer_contract: { summary: expectedContract },
        structured_contract: { sql_or_pseudocode: sqlOrPseudocode, expected_contract: expectedContract },
        ...refs,
      };
    }
    return {
      query_type: 'unstructured',
      business_question: businessQuestion,
      expected_answer_contract: { summary: expectedContract },
      unstructured_contract: { source_expectations: expectedContract, citation_rules: citationRules },
      citation_expectations: citationRules ? { rules: citationRules } : null,
      ...refs,
    };
  };

  const submitManual = async () => {
    if (!businessQuestion.trim()) return;
    await onCreate(buildPayload());
    resetForm();
  };

  const draftWithAI = async () => {
    if (!businessQuestion.trim()) return;
    await onDraftWithAI({ query_type: tab, business_question: businessQuestion });
    resetForm();
  };

  const startEdit = (query: WorkspaceVerifiedQuery) => {
    setEditingId(query.id);
    setEditQuestion(query.business_question);
    setEditContract(contractSummary(query));
  };

  const saveEdit = async (query: WorkspaceVerifiedQuery) => {
    await onUpdate(query.id, {
      business_question: editQuestion,
      expected_answer_contract: { summary: editContract },
      structured_contract:
        query.query_type === 'structured' || query.query_type === 'blended'
          ? { ...(query.structured_contract ?? {}), expected_contract: editContract }
          : query.structured_contract ?? undefined,
      unstructured_contract:
        query.query_type === 'unstructured' || query.query_type === 'blended'
          ? { ...(query.unstructured_contract ?? {}), source_expectations: editContract }
          : query.unstructured_contract ?? undefined,
    });
    setEditingId(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await onDelete(deleteTarget.id);
    setDeleteTarget(null);
    if (editingId === deleteTarget.id) {
      setEditingId(null);
    }
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-full border border-gray-200 bg-white p-1 dark:border-dark-border dark:bg-dark-surface">
          {(['structured', 'unstructured'] as const).map((item) => (
            <button
              key={item}
              type="button"
              className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize ${
                tab === item
                  ? 'bg-charcoal text-white dark:bg-white dark:text-charcoal'
                  : 'text-gray-500 hover:text-charcoal dark:text-gray-400 dark:hover:text-gray-100'
              }`}
              onClick={() => setTab(item)}
            >
              {item}
            </button>
          ))}
        </div>
        <Badge variant="secondary">{filteredQueries.length} examples</Badge>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <label className="block">
            <span className="text-sm font-medium text-charcoal dark:text-gray-100">Business question</span>
            <Textarea className="mt-2 min-h-[120px]" value={businessQuestion} onChange={(event) => setBusinessQuestion(event.target.value)} />
          </label>
          <div className="space-y-3">
            <label className="block">
              <span className="text-sm font-medium text-charcoal dark:text-gray-100">Expected answer contract</span>
              <Textarea className="mt-2 min-h-[86px]" value={expectedContract} onChange={(event) => setExpectedContract(event.target.value)} />
            </label>
            {tab === 'structured' ? (
              <>
                <Input placeholder="SQL or pseudocode" value={sqlOrPseudocode} onChange={(event) => setSqlOrPseudocode(event.target.value)} />
                <Input placeholder="Table refs" value={tableRefs} onChange={(event) => setTableRefs(event.target.value)} />
                <Input placeholder="Column, metric, or join refs" value={columnRefs} onChange={(event) => setColumnRefs(event.target.value)} />
              </>
            ) : (
              <>
                <Input placeholder="RAG source refs" value={sourceRefs} onChange={(event) => setSourceRefs(event.target.value)} />
                <Input placeholder="Citation expectations" value={citationRules} onChange={(event) => setCitationRules(event.target.value)} />
              </>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" size="sm" disabled={isDrafting || !businessQuestion.trim()} onClick={draftWithAI}>
            <SparklesIcon className="h-4 w-4" />
            Draft with AI
          </Button>
          <Button type="button" variant="brand" size="sm" disabled={isSaving || !businessQuestion.trim()} onClick={submitManual}>
            Save for review
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400">
          Loading verified queries...
        </div>
      ) : filteredQueries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-8 text-center text-sm text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400">
          No {tab} examples yet.
        </div>
      ) : (
        <div className="space-y-3">
          {filteredQueries.map((query) => (
            <article key={query.id} className="grid gap-4 rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className="min-w-0">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge variant={statusVariant(query.status)}>{statusLabel(query.status)}</Badge>
                  <Badge variant="outline">{query.source_surface}</Badge>
                  {query.compiled_pack_version ? <Badge variant="success">pack v{query.compiled_pack_version}</Badge> : null}
                </div>
                {editingId === query.id ? (
                  <Textarea className="min-h-[92px]" value={editQuestion} onChange={(event) => setEditQuestion(event.target.value)} />
                ) : (
                  <h2 className="text-base font-semibold leading-6 text-charcoal dark:text-gray-100">{query.business_question}</h2>
                )}
                {query.paraphrases.length > 0 && (
                  <p className="mt-3 text-xs leading-5 text-gray-500 dark:text-gray-400">{query.paraphrases.join(' / ')}</p>
                )}
                {query.last_verified_at && (
                  <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">Verified {formatDate(query.last_verified_at)}</p>
                )}
              </div>

              <div className="min-w-0 space-y-3">
                {editingId === query.id ? (
                  <Textarea className="min-h-[120px]" value={editContract} onChange={(event) => setEditContract(event.target.value)} />
                ) : (
                  <ExpectedContract query={query} />
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  {editingId === query.id ? (
                    <>
                      <Button type="button" variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                      <Button type="button" variant="brand" size="sm" disabled={isSaving} onClick={() => saveEdit(query)}>Save edits</Button>
                    </>
                  ) : (
                    <Button type="button" variant="outline" size="sm" onClick={() => startEdit(query)}>Edit</Button>
                  )}
                  {!['approved', 'compiled'].includes(query.status) && (
                    <Button type="button" variant="brand" size="sm" disabled={isReviewing} onClick={() => onApprove(query.id)}>
                      <CheckIcon className="h-4 w-4" />
                      Approve
                    </Button>
                  )}
                  {query.status !== 'rejected' && (
                    <Button type="button" variant="outline" size="sm" disabled={isReviewing} onClick={() => onReject(query.id)}>
                      <XMarkIcon className="h-4 w-4" />
                      Reject
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isDeleting}
                    onClick={() => setDeleteTarget(query)}
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
      <Modal open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>Delete verified query?</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4 text-sm leading-6 text-gray-600 dark:text-gray-300">
              <p>
                This removes the verified query from the workspace authoring queue. Active runtime packs stay unchanged until a new pack is compiled and activated.
              </p>
              {deleteTarget ? (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-3 font-medium text-charcoal dark:border-dark-border dark:bg-dark-surface-2 dark:text-gray-100">
                  {deleteTarget.business_question}
                </div>
              ) : null}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDelete} disabled={isDeleting}>
              <TrashIcon className="h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete query'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </section>
  );
}

function ExpectedContract({ query }: { query: WorkspaceVerifiedQuery }) {
  return (
    <div className="space-y-2 text-sm leading-6 text-charcoal dark:text-gray-100">
      {query.sql_text || query.pseudocode ? (
        <pre className="max-h-48 overflow-auto rounded-md bg-gray-50 p-3 font-mono text-xs leading-5 text-gray-700 dark:bg-dark-surface-2 dark:text-gray-200">
          {query.sql_text || query.pseudocode}
        </pre>
      ) : null}
      <p>{contractSummary(query) || 'Review expected answer contract before approval.'}</p>
      <RefLine label="Tables" values={query.table_refs} />
      <RefLine label="Columns" values={query.column_refs} />
      <RefLine label="Sources" values={query.source_refs} />
    </div>
  );
}

function RefLine({ label, values }: { label: string; values: string[] }) {
  if (!values.length) return null;
  return <p className="text-xs text-gray-500 dark:text-gray-400">{label}: {values.join(', ')}</p>;
}

function contractSummary(query: WorkspaceVerifiedQuery): string {
  const summary = query.expected_answer_contract?.summary;
  if (typeof summary === 'string') return summary;
  const structured = query.structured_contract?.expected_contract;
  if (typeof structured === 'string') return structured;
  const unstructured = query.unstructured_contract?.source_expectations;
  if (typeof unstructured === 'string') return unstructured;
  return '';
}

function splitList(value: string): string[] {
  const seen = new Set<string>();
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter((item) => {
      const key = item.toLowerCase();
      if (!item || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function statusVariant(status: string): 'secondary' | 'success' | 'warning' | 'danger' | 'info' {
  if (status === 'approved' || status === 'compiled') return 'success';
  if (status === 'rejected') return 'danger';
  if (status === 'draft_ai') return 'info';
  if (status === 'pending_review') return 'warning';
  return 'secondary';
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}
